import localForage from 'localforage';
import * as Immutable from 'immutable';
import { ofType } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';
import { ID, ReduxAction } from '../types';
import Group from '@models/Group';
import BaseObject from '@models/BaseObject';
import { selectUser } from '@reducers/user/account';
import { selectCustomSourceLabels } from '@reducers/ui/sourceLabels';

/*
This namespace exports:
1. An action creator for loading the local store state, which is dispatched *once* when the app mounts
2. Redux middleware for asynchronously writing to the local store when the Redux state updates in response
   to successful API request responses
3. An epic that manages asynchronously reading local store state. Collection reducers should handle the
   'LOAD_LOCAL_STORE_SUCCEEDED' action individually. See @reducers/feeds/feedsReducer for an example.

To sync a new collection, add the resource type to the resourceTypeMatcher and include a getValue call
in the getResourcePromises list. The count of collection items to cache defaults to 25 but can be configured
below in collectionItemsToCache. We should try to only store what might appear above the fold in a given view;
this way we optimize the speed of the first render.

Pattern exceptions are handled individually in localStoreSync
*/

// action creator for dispatching load of any stored data
export const loadLocalStore = () => ({ type: 'LOAD_LOCAL_STORE' });

const getValue = async (userID: ID, resourceType: string): Promise<[string, any[]]> => {
  if (userID) {
    const collection = await localForage.getItem(`clay_${userID}_${resourceType}`);
    return [resourceType, collection] as [string, any[]];
  } else {
    return new Promise(() => [resourceType, null]);
  }
};

const setValue = (userID: ID, resourceType: string, value: any): void => {
  localForage
    .setItem(`clay_${userID}_${resourceType}`, value)
    .catch((err) => console.error('Failed to cache data in LocalForage instance', err));
};

const updateValue = async (userID: ID, resourceType: string, valueUpdate: Record<string, any>) => {
  const [_, currentValue] = await getValue(userID, resourceType);
  setValue(userID, resourceType, { ...(currentValue || {}), ...valueUpdate });
};

const clearKey = (userID: ID, resourceType: string) => {
  localForage.removeItem(`clay_${userID}_${resourceType}`);
};

// Store collections as arrays. ImmutableJS data structures are converted to POJOs when inserted into the localForage instance.
const setCollection = (
  userID: ID,
  resourceType: string,
  collection: Immutable.OrderedMap<ID, any>,
): void => {
  let collectionValuesArr = collection.valueSeq().toArray();

  // We use an OrderedMap for storing contacts on groups. Convert these for creating new groups with contacts
  // on cache load

  if (resourceType == 'groups') {
    collectionValuesArr = collectionValuesArr.map(
      ({ id, title, color, reconnect_cadence, slug, memberIDs }: Group) => {
        return {
          id,
          title,
          color,
          reconnect_cadence,
          slug,
          contact_ids: memberIDs.toArray(),
        };
      },
    );
  }

  setValue(userID, resourceType, collectionValuesArr);
};

const resourceTypeMatcher = /(FEED|INBOX|GROUP|NOTES|REMINDERS)/;
const resourceTypeToReduxSlice = {
  FEED: 'feed',
  REMINDERS: 'reminders',
  INBOX: 'inbox',
  GROUP: 'groups',
  GROUP_SORTING: 'group_sorting',
  NOTES: 'notes',
  SEARCH_RECENTS: 'search_recent',
  SEARCH_SORTING: 'search_sorting',
  PEOPLE_SORTING: '_all_people_search_sorting',
  NEW_NOTE_DRAFT: 'new_note_draft',
  SYSTEM_ALERT: 'last_dismissed_system_alert',
  NAV_BAR_STATUS: 'nav_bar_status',
  NAV_BAR_WIDTH: 'nav_bar_width',
  RIGHT_SIDEBAR_WIDTH: 'right_sidebar_width',
  SOURCE_LABELS: 'source_labels',
};

const deprecatedStorageSlices = ['feeds'];

const SYNC_ON_DELETE_SUCEEDED = ['DELETE_INBOX_ITEM_SUCCEEDED', 'DELETE_INBOX_SUCCEEDED'];

const getResourcePromises = (userId: string) => {
  deprecatedStorageSlices.map((slice) => clearKey(userId, slice));

  return Object.values(resourceTypeToReduxSlice).map((slice) => getValue(userId, slice));
};

const collectionItemsToCache = {
  feed: 50,
  inbox: 50,
  groups: 1000,
  notes: 50,
  reminders: 100,
};

// Middleware for syncing local state to the redux store
export const localStoreSync = (store) => (next) => (action) => {
  const [_, methodAndResource, succeeded] = action.type.match(/(.*)_(SUCCEEDED)/) || [];
  let [resourceType] = (methodAndResource || '').match(resourceTypeMatcher) || [];
  let userID = store.getState().user.account.id;

  // We only want to update the cache when the notes index GET succeeds
  if (/CONTACT_NOTES/.test(action.type)) {
    resourceType = null;
  }

  next(action);

  if ((succeeded && resourceType && userID) || SYNC_ON_DELETE_SUCEEDED.includes(action.type)) {
    const slice = resourceTypeToReduxSlice[resourceType];
    const sliceState = store.getState().data[slice];
    let collection = sliceState;

    if (slice === 'reminders') {
      collection = collection.items;
    } else {
      collection = collection.collection?.items || collection.items;
    }

    const storableCollection = collection
      .take(collectionItemsToCache[slice] || 25)
      .map((entity: BaseObject) => entity.toObj);
    setCollection(userID, slice, storableCollection);
  }

  /* Pattern exceptions */
  if (
    (userID && action.type === 'SAVE_SEARCH_TERM') ||
    (action.type === 'SET_SEARCH_TERM' && action.payload.updateRecent) ||
    action.type === 'CLEAR_SEARCH_HISTORY' ||
    action.type === 'REMOVE_SEARCH_TERM'
  ) {
    setValue(userID, 'search_recent', store.getState().data.search.recent);
  }

  if (userID && action.type === 'SET_SEARCH_SORTING') {
    setValue(userID, 'search_sorting', store.getState().data.search.sorting);
  }

  if (userID && action.type === 'SET__ALL_PEOPLE_SEARCH_SORTING') {
    setValue(userID, '_all_people_search_sorting', store.getState().data.allPeopleSearch.sorting);
  }

  if (userID && action.type === 'SET_GROUP_SORTING') {
    updateValue(userID, 'group_sorting', { [action.payload.groupID]: action.payload.sorting });
  }

  if (userID && ['UPDATE_CONTACT_AND_NOTE_MODAL', 'CREATE_NOTE_FAILED'].includes(action.type)) {
    setValue(userID, 'new_note_draft', store.getState().ui.modal.newNoteDraft);
  }

  if (
    userID &&
    ['CLOSE_MODAL_AND_CLEAR_NOTE_DRAFT', 'CREATE_NOTE_SUCCEEDED'].includes(action.type)
  ) {
    clearKey(userID, 'new_note_draft');
  }

  if (userID && action.type === 'DISMISS_SYSTEM_ALERT') {
    setValue(
      userID,
      'last_dismissed_system_alert',
      store.getState().ui.alerts.systemAlert?.publishedAt,
    );
  }

  if (userID && action.type === 'NAV_BAR_FINISHED_TRANSITION') {
    setValue(
      userID,
      'nav_bar_status',
      store.getState().ui.navBar.status === 'pinned' ? 'pinned' : 'hidden',
    );
  }

  if (userID && action.type === 'RESIZE_NAV_BAR') {
    setValue(userID, 'nav_bar_width', action.payload.width);
  }

  if (userID && action.type === 'RESIZE_RIGHT_SIDEBAR') {
    setValue(userID, 'right_sidebar_width', action.payload.width);
  }

  if (userID && ['ADD_SOURCE_LABEL', 'REMOVE_SOURCE_LABEL'].includes(action.type)) {
    const existingSourceTypeLabels = selectCustomSourceLabels(action.payload.sourceType)(
      store.getState(),
    ).toArray();

    if (action.type === 'ADD_SOURCE_LABEL') {
      updateValue(userID, 'source_labels', {
        [action.payload.sourceType]: existingSourceTypeLabels,
      });
    }

    if (action.type === 'REMOVE_SOURCE_LABEL') {
      updateValue(userID, 'source_labels', {
        [action.payload.sourceType]: existingSourceTypeLabels.filter(
          (label) => label !== action.payload.label,
        ),
      });
    }
  }
};

// Epic that manages async reading of the locally stored data.
// Individual reducers can respond to the LOAD_LOCAL_STORE_SUCCEEDED action by writing collection data
// if the related collection has not yet been loaded from the server.
export const localStoreEpic = (action$: Observable<ReduxAction>, state$) =>
  action$.pipe(
    ofType('LOAD_LOCAL_STORE'),
    // if there is no current session with a User ID, ignore this action altogether
    filter(() => !!selectUser(state$.value)?.id),
    mergeMap(() =>
      from(Promise.all(getResourcePromises(String(selectUser(state$.value)?.id)))).pipe(
        map((collections) => ({
          type: 'LOAD_LOCAL_STORE_SUCCEEDED',
          payload: new Map(collections),
        })),
        catchError((_err) => of({ type: 'LOAD_LOCAL_STORE_FAILED' })),
      ),
    ),
  );
